'use client';

import { useState, useEffect, useMemo } from 'react';
import toast from 'react-hot-toast';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import type { LogisticsJob } from './types';

type InspectionType = 'pre_install' | 'mid_install' | 'final' | 'warranty' | 'courtesy';

const INSPECTION_TYPE_OPTIONS: Array<{ key: InspectionType; label: string; color: string; bg: string }> = [
  { key: 'pre_install',  label: 'Pre-Install',    color: '#2563eb', bg: '#dbeafe' },
  { key: 'mid_install',  label: 'Mid-Install',    color: '#F5A623', bg: '#fef3c7' },
  { key: 'final',        label: 'Final QC',       color: '#16a34a', bg: '#dcfce7' },
  { key: 'warranty',     label: 'Warranty Check', color: '#7c3aed', bg: '#ede9fe' },
  { key: 'courtesy',     label: 'Courtesy',       color: '#6b7280', bg: '#f3f4f6' },
];

interface ScheduleInspectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  jobs: LogisticsJob[];
  preselectedJobId?: string;
  onScheduled?: (inspectionId: string) => void;
}

const ScheduleInspectionModal = ({ isOpen, onClose, jobs, preselectedJobId, onScheduled }: ScheduleInspectionModalProps) => {
  const [selectedJobId, setSelectedJobId] = useState<string>('');
  const [inspectionType, setInspectionType] = useState<InspectionType>('final');
  const [date, setDate] = useState<string>('');
  const [time, setTime] = useState<string>('09:00');
  // TODO: replace inspectorName free-text with TeamMemberPicker once user_profiles roster is populated
  const [inspectorName, setInspectorName] = useState<string>('');
  const [notes, setNotes] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);

  const supabase = createClient();
  const { currentUser } = useAuth();

  const selectedJob = useMemo(
    () => jobs.find(j => j.job_id === selectedJobId) ?? null,
    [jobs, selectedJobId]
  );

  // Read auto-fill context from contact_directory with safe optional chaining
  const jobContext = useMemo(() => {
    if (!selectedJob) return null;
    const cd: any = (selectedJob as any).contact_directory ?? {};
    return {
      address: selectedJob.address ?? '',
      lat: (selectedJob as any).latitude ?? 0,
      lng: (selectedJob as any).longitude ?? 0,
      homeownerName: cd?.homeowner?.name ?? '',
      homeownerPhone: cd?.homeowner?.phone ?? '',
      pmName: cd?.pm?.name ?? (selectedJob as any).vpm_name ?? '',
      pmPhone: cd?.pm?.phone ?? '',
      foremanName: cd?.foreman?.name ?? (selectedJob as any).lead_installer_name ?? '',
      foremanPhone: cd?.foreman?.phone ?? '',
    };
  }, [selectedJob]);

  // Reset on open
  useEffect(() => {
    if (!isOpen) {
      const t = setTimeout(() => {
        setSelectedJobId('');
        setInspectionType('final');
        setDate('');
        setTime('09:00');
        setInspectorName('');
        setNotes('');
        setSubmitting(false);
      }, 200);
      return () => clearTimeout(t);
    } else {
      // Default date: 3 business days out
      const d = new Date();
      d.setDate(d.getDate() + 3);
      setDate(d.toISOString().slice(0, 10));
      // Apply preselected job if provided
      if (preselectedJobId) setSelectedJobId(preselectedJobId);
    }
  }, [isOpen, preselectedJobId]);

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape' && !submitting) onClose(); };
    document.addEventListener('keydown', h);
    return () => document.removeEventListener('keydown', h);
  }, [isOpen, submitting, onClose]);

  const isValid =
    !!selectedJobId &&
    !!inspectionType &&
    !!date &&
    !!time &&
    inspectorName.trim().length >= 2 &&
    !submitting;

  const handleSubmit = async () => {
    if (!isValid || !selectedJob || !jobContext) return;
    setSubmitting(true);

    const scheduledAt = new Date(`${date}T${time}:00`).toISOString();
    const reporterName = `${currentUser?.first_name ?? ''} ${currentUser?.last_name ?? ''}`.trim();

    const insertPayload: any = {
      // id auto-generated by gen_random_uuid()::text default
      job_id: selectedJob.job_id,
      inspection_type: inspectionType,
      assigned_inspector_id: inspectorName.trim().toLowerCase().replace(/\s+/g, '-'),
      address: jobContext.address,
      lat: jobContext.lat,
      lng: jobContext.lng,
      status: 'Ready for QC',
      homeowner_name: jobContext.homeownerName,
      homeowner_phone: jobContext.homeownerPhone,
      pm_name: jobContext.pmName,
      pm_phone: jobContext.pmPhone,
      foreman_name: jobContext.foremanName,
      foreman_phone: jobContext.foremanPhone,
      inspection_date: scheduledAt,
      notes: notes.trim() || `Inspector: ${inspectorName.trim()}${reporterName ? ` • Scheduled by: ${reporterName}` : ''}`,
    };

    try {
      const { data, error } = await supabase
        .from('qc_inspections')
        .insert(insertPayload)
        .select('id')
        .single();

      if (error) throw error;

      const newInspectionId = data?.id ?? '';
      const typeLabel = INSPECTION_TYPE_OPTIONS.find(o => o.key === inspectionType)?.label ?? inspectionType;
      const dateLabel = new Date(scheduledAt).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
      toast.success(`${typeLabel} scheduled for ${selectedJob.job_id} on ${dateLabel}`, {
        duration: 4000,
        icon: '📅',
      });
      onScheduled?.(newInspectionId);
      setSubmitting(false);
      onClose();
    } catch (err) {
      console.error('Failed to schedule inspection:', err);
      const message = err instanceof Error ? err.message : 'Unknown error';
      toast.error(`Schedule failed: ${message}`, { duration: 6000 });
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const submitLabel = submitting ? 'Scheduling…' : 'Schedule Inspection';

  return (
    <div
      className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[9999] flex items-center justify-center p-4"
      onClick={submitting ? undefined : onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="schedule-inspection-fab-title"
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-xl max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between p-6 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
              <svg className="w-5 h-5 text-green-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
                <rect x="3" y="4" width="18" height="18" rx="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M16 2v4M8 2v4M3 10h18M9 16l2 2 4-4" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <div>
              <h3 id="schedule-inspection-fab-title" className="text-lg font-bold text-[#1C2E5E]">Schedule QC Inspection</h3>
              <p className="text-[12px] text-gray-500 mt-0.5">Book an inspection on any active job</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50"
            aria-label="Close modal"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <path d="M18 6 6 18M6 6l12 12" strokeLinecap="round"/>
            </svg>
          </button>
        </div>

        {/* Form body */}
        <div className="p-6 space-y-4">
          {/* Job dropdown */}
          <div>
            <label htmlFor="si-job" className="block text-[12px] font-semibold text-gray-700 mb-1.5">
              Job <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <select
                id="si-job"
                value={selectedJobId}
                onChange={e => setSelectedJobId(e.target.value)}
                className="w-full pl-3 pr-8 py-2 text-[13px] rounded-lg border border-gray-200 bg-gray-50 focus:bg-white focus:border-[#E89A3C] focus:outline-none focus:ring-2 focus:ring-[#E89A3C]/20 transition-all appearance-none cursor-pointer"
              >
                <option value="">— Select a job —</option>
                {jobs.map(j => (
                  <option key={j.id} value={j.job_id}>
                    {j.job_id} — {j.address}
                  </option>
                ))}
              </select>
              <svg className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <path d="m6 9 6 6 6-6" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
          </div>

          {/* Inspection Type pills */}
          <div>
            <label className="block text-[12px] font-semibold text-gray-700 mb-1.5">
              Inspection Type <span className="text-red-500">*</span>
            </label>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
              {INSPECTION_TYPE_OPTIONS.map(opt => {
                const active = inspectionType === opt.key;
                return (
                  <button
                    key={opt.key}
                    type="button"
                    onClick={() => setInspectionType(opt.key)}
                    className="px-2 py-2 rounded-lg text-[11px] font-bold transition-all"
                    style={{
                      background: active ? opt.color : opt.bg,
                      color: active ? '#fff' : opt.color,
                      boxShadow: active ? `0 4px 12px ${opt.color}44` : 'none',
                    }}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Date + Time */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label htmlFor="si-date" className="block text-[12px] font-semibold text-gray-700 mb-1.5">
                Date <span className="text-red-500">*</span>
              </label>
              <input
                id="si-date"
                type="date"
                value={date}
                onChange={e => setDate(e.target.value)}
                className="w-full px-3 py-2 text-[13px] rounded-lg border border-gray-200 bg-gray-50 focus:bg-white focus:border-[#E89A3C] focus:outline-none focus:ring-2 focus:ring-[#E89A3C]/20 transition-all"
              />
            </div>
            <div>
              <label htmlFor="si-time" className="block text-[12px] font-semibold text-gray-700 mb-1.5">
                Time <span className="text-red-500">*</span>
              </label>
              <input
                id="si-time"
                type="time"
                value={time}
                onChange={e => setTime(e.target.value)}
                className="w-full px-3 py-2 text-[13px] rounded-lg border border-gray-200 bg-gray-50 focus:bg-white focus:border-[#E89A3C] focus:outline-none focus:ring-2 focus:ring-[#E89A3C]/20 transition-all"
              />
            </div>
          </div>

          {/* Inspector Name (free text — TODO: TeamMemberPicker later) */}
          <div>
            <label htmlFor="si-inspector" className="block text-[12px] font-semibold text-gray-700 mb-1.5">
              Inspector Name <span className="text-red-500">*</span>
            </label>
            <input
              id="si-inspector"
              type="text"
              value={inspectorName}
              onChange={e => setInspectorName(e.target.value)}
              placeholder="e.g. Inspector Martinez"
              maxLength={80}
              className="w-full px-3 py-2 text-[13px] rounded-lg border border-gray-200 bg-gray-50 focus:bg-white focus:border-[#E89A3C] focus:outline-none focus:ring-2 focus:ring-[#E89A3C]/20 transition-all"
            />
            <p className="text-[10px] text-gray-400 mt-1">Free text for now. Will become a team picker once user profiles are live.</p>
          </div>

          {/* Auto-populated job context block — only when a job is selected */}
          {selectedJob && jobContext && (
            <div className="rounded-lg bg-gray-50 border border-gray-100 p-3 space-y-2">
              <p className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Auto-filled from job</p>
              <div className="text-[12px] text-gray-700">
                <p className="font-semibold">{jobContext.address}</p>
              </div>
              <div className="grid grid-cols-2 gap-2 text-[11px]">
                {jobContext.homeownerName && (
                  <div>
                    <span className="text-[9px] font-bold uppercase tracking-wider text-gray-500">Homeowner</span>
                    <p className="text-gray-700">{jobContext.homeownerName}</p>
                    {jobContext.homeownerPhone && <p className="text-gray-500">{jobContext.homeownerPhone}</p>}
                  </div>
                )}
                {jobContext.pmName && (
                  <div>
                    <span className="text-[9px] font-bold uppercase tracking-wider text-gray-500">PM</span>
                    <p className="text-gray-700">{jobContext.pmName}</p>
                    {jobContext.pmPhone && <p className="text-gray-500">{jobContext.pmPhone}</p>}
                  </div>
                )}
                {jobContext.foremanName && (
                  <div>
                    <span className="text-[9px] font-bold uppercase tracking-wider text-gray-500">Foreman</span>
                    <p className="text-gray-700">{jobContext.foremanName}</p>
                    {jobContext.foremanPhone && <p className="text-gray-500">{jobContext.foremanPhone}</p>}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Notes */}
          <div>
            <label htmlFor="si-notes" className="block text-[12px] font-semibold text-gray-700 mb-1.5">
              Notes <span className="font-normal text-gray-400">(optional)</span>
            </label>
            <textarea
              id="si-notes"
              value={notes}
              onChange={e => setNotes(e.target.value)}
              rows={3}
              maxLength={1000}
              placeholder="Special instructions, access notes, what to focus on…"
              className="w-full px-3 py-2 text-[13px] rounded-lg border border-gray-200 bg-gray-50 focus:bg-white focus:border-[#E89A3C] focus:outline-none focus:ring-2 focus:ring-[#E89A3C]/20 transition-all resize-none"
            />
            <p className="text-[11px] text-gray-400 mt-1">{notes.length}/1000</p>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 p-6 border-t border-gray-100">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="px-4 py-2 rounded-lg text-[13px] font-semibold text-gray-600 hover:bg-gray-100 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!isValid || submitting}
            className="px-4 py-2 rounded-lg text-[13px] font-semibold text-white inline-flex items-center gap-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            style={{
              background: isValid && !submitting ? '#16a34a' : '#9ca3af',
              boxShadow: isValid && !submitting ? '0 4px 12px rgba(22, 163, 74, 0.25)' : 'none',
            }}
          >
            {submitting && (
              <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeOpacity="0.25" strokeWidth="3"/>
                <path d="M12 2a10 10 0 0 1 10 10" stroke="currentColor" strokeWidth="3" strokeLinecap="round"/>
              </svg>
            )}
            {submitLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ScheduleInspectionModal;
